import type {
  HostedEvent,
  HostedEventPlace,
  HostedEventUpdate,
  NewHostedEvent,
  OwedRefund,
} from "@hobbyshopify/backend-client";
import type { GeoRegionList } from "../repositories/countries-repository";
import { draftToNewLocation, type LocationDraft, type LocationErrors, validateLocationDraft } from "./location-form";
import { formatAmount } from "./money";

/** The show form. Dates and times are the venue's wall clock, in `timezone`. */
export interface ShowDraft {
  readonly title: string;
  readonly description: string;
  readonly startDate: string;
  readonly startTime: string;
  readonly endDate: string;
  readonly endTime: string;
  readonly timezone: string;
  readonly shareUrl: string;
  /** A location id, `NEW_VENUE`, or "" until chosen. Only a new show has one. */
  readonly venue: string;
}

export type ShowField = keyof ShowDraft;
export type ShowErrors = Partial<Record<ShowField, string>>;

export const NEW_VENUE = "new";

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const TIME = /^\d{2}:\d{2}$/;

export function emptyShow(timezone: string): ShowDraft {
  return {
    title: "",
    description: "",
    startDate: "",
    startTime: "09:00",
    endDate: "",
    endTime: "17:00",
    timezone,
    shareUrl: "",
    venue: "",
  };
}

/** Prefills the form from a saved show, its times on its own clock. */
export function showDraft(event: HostedEvent, fallbackZone: string): ShowDraft {
  const timezone = event.timezone ?? fallbackZone;
  const start = wallClock(event.startsAt, timezone);
  const end = wallClock(event.endsAt, timezone);
  return {
    title: event.title,
    description: event.description ?? "",
    startDate: start.date,
    startTime: start.time,
    endDate: end.date,
    endTime: end.time,
    timezone,
    shareUrl: event.shareUrl ?? "",
    venue: "",
  };
}

export function isTimeZone(zone: string): boolean {
  if (!zone) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/**
 * The form's own checks, ahead of the server's: which field is wrong, while the
 * merchant is still looking at it. A new show also needs its venue chosen, and
 * no show is saved already over: it could not be published.
 */
export function validateShowDraft(draft: ShowDraft, creating: boolean, now: Date): ShowErrors {
  const errors: ShowErrors = {};
  if (!draft.title.trim()) errors.title = "Name the show.";
  if (creating && !draft.venue) errors.venue = "Choose where the show is.";
  if (!DATE.test(draft.startDate)) errors.startDate = "Choose the day it starts.";
  if (!TIME.test(draft.startTime)) errors.startTime = "Choose when it opens.";
  if (!DATE.test(draft.endDate)) errors.endDate = "Choose the day it ends.";
  if (!TIME.test(draft.endTime)) errors.endTime = "Choose when it closes.";
  if (!isTimeZone(draft.timezone)) errors.timezone = "Choose the time zone.";
  const url = draft.shareUrl.trim();
  if (url && !/^https?:\/\/\S+$/i.test(url)) errors.shareUrl = "Enter a link that starts with http:// or https://.";
  if (!errors.startDate && !errors.startTime && !errors.endDate && !errors.endTime && !errors.timezone) {
    const { startsAt, endsAt } = showInstants(draft);
    if (endsAt.getTime() <= startsAt.getTime()) errors.endTime = "A show ends after it starts.";
    else if (endsAt.getTime() <= now.getTime()) errors.endDate = SHOW_ENDS_IN_THE_PAST;
  }
  return errors;
}

export const SHOW_ENDS_IN_THE_PAST = "This has already passed. Choose an end still to come.";

/** The fields that say when a show is: a change to any of them can settle whether its end has passed. */
export const SHOW_WHEN_FIELDS: readonly ShowField[] = ["startDate", "startTime", "endDate", "endTime", "timezone"];

/** The address rules a new location follows, in the venue's own words. */
export function validateVenueDraft(venue: LocationDraft, regions: GeoRegionList): LocationErrors {
  const errors = validateLocationDraft(venue, regions);
  if (errors.name) errors.name = "Name the venue.";
  if (errors.countryCode) errors.countryCode = "Choose the country the venue is in.";
  return errors;
}

export function showInstants(draft: ShowDraft): { startsAt: Date; endsAt: Date } {
  return {
    startsAt: zonedInstant(draft.startDate, draft.startTime, draft.timezone),
    endsAt: zonedInstant(draft.endDate, draft.endTime, draft.timezone),
  };
}

export function draftToNewShow(draft: ShowDraft, venue: LocationDraft): NewHostedEvent {
  const description = draft.description.trim();
  const shareUrl = draft.shareUrl.trim();
  const fields = {
    title: draft.title.trim(),
    ...showInstants(draft),
    timezone: draft.timezone,
    ...(description ? { description } : {}),
    ...(shareUrl ? { shareUrl } : {}),
  };
  if (draft.venue !== NEW_VENUE) return { ...fields, locationId: draft.venue };
  const { phone: _, openingHours: __, ...address } = draftToNewLocation(venue);
  return { ...fields, venue: address };
}

/** Edits send every field: a blank description or link clears it. */
export function draftToShowUpdate(draft: ShowDraft): HostedEventUpdate {
  return {
    title: draft.title.trim(),
    description: draft.description.trim() || null,
    ...showInstants(draft),
    timezone: draft.timezone,
    shareUrl: draft.shareUrl.trim() || null,
  };
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function clockIn(zone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(zone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatters.set(zone, formatter);
  }
  return formatter;
}

function clockParts(instant: number, zone: string) {
  const parts = clockIn(zone).formatToParts(new Date(instant));
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "00";
  return {
    year: part("year"),
    month: part("month"),
    day: part("day"),
    hour: String(Number(part("hour")) % 24).padStart(2, "0"),
    minute: part("minute"),
    second: part("second"),
  };
}

/** Offset of `zone` from UTC at `instant`, in milliseconds, positive east. */
function offsetAt(instant: number, zone: string): number {
  const p = clockParts(instant, zone);
  const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  return asUtc - Math.floor(instant / 1000) * 1000;
}

/**
 * The instant the clock in `zone` reads `date` (YYYY-MM-DD) and `time` (HH:mm).
 * The offset is applied twice, the second time as it stands at the first guess,
 * which settles every time but the hour a DST change skips or repeats.
 */
export function zonedInstant(date: string, time: string, zone: string): Date {
  const [year, month, day] = date.split("-").map(Number);
  const [hour, minute] = time.split(":").map(Number);
  const naive = Date.UTC(year, month - 1, day, hour, minute);
  const firstGuess = naive - offsetAt(naive, zone);
  return new Date(naive - offsetAt(firstGuess, zone));
}

/** What the clock in `zone` reads at `instant`, as the form's date and time. */
export function wallClock(instant: Date, zone: string): { date: string; time: string } {
  const p = clockParts(instant.getTime(), zone);
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
}

/** The US zones first, then every zone the browser knows; `current` is always offered. */
export function timeZoneChoices(current: string): string[] {
  const us = [
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Phoenix",
    "America/Los_Angeles",
    "America/Anchorage",
    "Pacific/Honolulu",
  ];
  const intl = Intl as typeof Intl & { supportedValuesOf?: (key: "timeZone") => string[] };
  const all = intl.supportedValuesOf?.("timeZone") ?? [];
  return [...new Set([...(current ? [current] : []), ...us, ...all])];
}

/** "Sat, Nov 7, 2026 · 9:00 AM – 5:00 PM CST", on the show's own clock. */
export function showDates(event: Pick<HostedEvent, "startsAt" | "endsAt" | "timezone">): string {
  const timeZone = event.timezone ?? undefined;
  const day = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const time = new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit" });
  const zoned = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
  const startDay = day.format(event.startsAt);
  const endDay = day.format(event.endsAt);
  if (startDay === endDay) return `${startDay} · ${time.format(event.startsAt)} – ${zoned.format(event.endsAt)}`;
  return `${startDay}, ${time.format(event.startsAt)} – ${endDay}, ${zoned.format(event.endsAt)}`;
}

export function venueLine(place: HostedEventPlace): string {
  return [place.name, place.address1, place.city, place.province].filter(Boolean).join(", ");
}

export interface ShowStatus {
  readonly label: string;
  readonly cls: string;
}

export function showStatus(event: Pick<HostedEvent, "isPublished" | "isBlocked" | "cancelledAt">): ShowStatus {
  if (event.cancelledAt) return { label: "Cancelled", cls: "badge-warn" };
  if (event.isBlocked) return { label: "Blocked by HobbyScan", cls: "badge-danger" };
  if (event.isPublished) return { label: "Published", cls: "badge-accent" };
  return { label: "Draft", cls: "badge-neutral" };
}

const count = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/**
 * What the host reads before cancelling: what becomes of the tables, and that
 * it is final. `paidTables` is null while the tables are not read.
 */
export function cancelWarning(paidTables: number | null): string {
  const paid =
    paidTables === null
      ? "Every paid table is refunded in full, fees included."
      : paidTables > 0
        ? `${count(paidTables, "paid table")} ${paidTables === 1 ? "is" : "are"} refunded in full, fees included.`
        : "No shop has paid for a table yet.";
  return `${paid} Tables being paid for are let go, the booths at the show close and it leaves the calendar. This cannot be undone.`;
}

const tryAgain = (owed: number) =>
  `Try again, or refund ${owed === 1 ? "that shop" : "those shops"} in your Stripe dashboard.`;

/** What a cancel did with the payments for the show's tables. */
export function cancelledText({ refunded, refundsFailed }: { refunded: number; refundsFailed: number }): string {
  if (refundsFailed > 0) {
    return `The show is cancelled, but ${count(refundsFailed, "refund")} did not go through. ${tryAgain(refundsFailed)}`;
  }
  if (refunded > 0) return `The show is cancelled. ${count(refunded, "payment")} went back to the shops.`;
  return "The show is cancelled.";
}

/** What trying the refunds owed again did. */
export function retriedText({ refunded, refundsFailed }: { refunded: number; refundsFailed: number }): string {
  if (refundsFailed > 0) {
    return `${count(refundsFailed, "refund")} still did not go through. ${tryAgain(refundsFailed)}`;
  }
  return refunded > 0 ? `${count(refunded, "payment")} went back to the shops.` : "Nothing was owed any more.";
}

/** A refund the show owes, as the host's list names it: "Rookie Row · table 2 (Wall) · $81.31". */
export function owedLine(refund: OwedRefund): string {
  const who = refund.shop ? (refund.shop.name ?? "A shop") : (refund.email ?? "A buyer");
  return `${who} · table ${refund.tableLabel} (${refund.kindName}) · ${formatAmount(refund.amountCents, refund.currency)}`;
}

/** Why a refund is owed, and where it stands: not sent yet, or what stopped the last try. */
export function owedState(refund: OwedRefund): string {
  const why = refund.refundReason ? `${refund.refundReason} ` : "";
  return refund.refundError ? `${why}Not sent: ${refund.refundError}` : `${why}Not sent yet.`;
}

/** "2 refunds owed", on a show's row. */
export function owedBadge(owed: number): string {
  return `${count(owed, "refund")} owed`;
}

export interface ShowMay {
  readonly edit: boolean;
  readonly publish: boolean;
  readonly takeDown: boolean;
  readonly cancel: boolean;
  /** Not listed and over: it cannot be published until its dates are moved. */
  readonly datesPassed: boolean;
}

export const SHOW_MAY_NOTHING: ShowMay = {
  edit: false,
  publish: false,
  takeDown: false,
  cancel: false,
  datesPassed: false,
};

/**
 * What a show's page offers its host now: a show over or cancelled is no longer edited, published or
 * called off. One exception, so that a show nobody came to is not stuck: a show that is over, not listed
 * and with no paid table (`paidTables`, null while the tables are not read) can still be edited, to move
 * its dates; until then `datesPassed` says why it cannot be published.
 */
export function showMay(
  event: Pick<HostedEvent, "isPublished" | "isBlocked" | "cancelledAt" | "endsAt">,
  now: Date,
  paidTables: number | null,
): ShowMay {
  const over = event.endsAt.getTime() < now.getTime();
  const open = !event.cancelledAt && !over;
  const movable = !event.cancelledAt && over && !event.isPublished && paidTables === 0;
  return {
    edit: open || movable,
    publish: open && !event.isPublished && !event.isBlocked,
    takeDown: event.isPublished,
    cancel: open,
    datesPassed: movable && !event.isBlocked,
  };
}

/** A refund call failed on its way: trying again is safe, since a payment is never refunded twice. */
export const REFUND_FAILED_ON_THE_WAY =
  "Something went wrong while refunding. Try again: a payment is never refunded twice.";

/** Sending the refunds owed failed on its way. */
export const REFUNDS_FAILED_ON_THE_WAY =
  "Something went wrong while sending the refunds. Try again: a payment is never refunded twice.";

/** A cancel failed on its way: cancelling again finishes what is left. */
export const CANCEL_FAILED_ON_THE_WAY =
  "Something went wrong while cancelling. Try again: cancelling again finishes what is left, and a payment is never refunded twice.";

/** A show, or the shop's shows, could not be read, for a reason other than a refusal. */
export const SHOW_FAILED_TO_LOAD = "The show could not be read. Reload the page to try again.";
export const SHOWS_FAILED_TO_LOAD = "The shows could not be read. Reload the page to try again.";

/** A publish or a take-down failed on its way; either can be asked for again. */
export const PUBLISH_FAILED_ON_THE_WAY = "Something went wrong on the way. Try again if the show did not change.";

/** A save failed on its way: the show may have been saved all the same. */
export const SAVE_FAILED_ON_THE_WAY =
  "Something went wrong while saving. Look at your shows before trying again: this one may have been saved.";

/** Shows still to come, soonest first, and the ones over, latest first. */
export function splitShows(
  events: readonly HostedEvent[],
  now: Date,
): { upcoming: HostedEvent[]; past: HostedEvent[] } {
  const upcoming = events.filter((e) => e.endsAt.getTime() >= now.getTime());
  const past = events.filter((e) => e.endsAt.getTime() < now.getTime()).reverse();
  return { upcoming, past };
}
